import { format } from 'date-fns/format'
import { isAfter } from 'date-fns/isAfter'
import { isBefore } from 'date-fns/isBefore'
import { isValid } from 'date-fns/isValid'
import { parse } from 'date-fns/parse'
import { sub } from 'date-fns/sub'
import { subYears } from 'date-fns/subYears'
import _get from 'lodash/get'
import _set from 'lodash/set'
import * as z from 'zod'
import type {
  ZodDate,
  ZodEffects,
  ZodEnum,
  ZodNumber,
  ZodObject,
  ZodOptional,
  ZodRawShape,
  ZodTypeAny,
} from 'zod'

import * as constants from '@nxt-insurance/constants'

import type { StepConfigParams } from '@flows/configs/types'
import { t } from '@flows/core/i18n'
import * as dateUtils from '@flows/insurance/utils/dateUtils'

export const createPreprocessedDateSchema = (
  // ZodTypeAny because we only care about the input and output
  schema: ZodDate | ZodEffects<ZodTypeAny, Date, Date> | ZodOptional<ZodDate>,
  dateFormat: string = constants.SERVER_DATE_FORMAT
) =>
  z
    .preprocess((value) => {
      if (value === '' || value === undefined) {
        return undefined
      }

      if (typeof value === 'string') {
        // Parse the date in accordance with the passed format, and if it does not match, invalid date is returned
        const parsedDate = parse(value, dateFormat, new Date())

        return new Date(parsedDate)
      }

      if (value instanceof Date) {
        return new Date(value)
      }
      return value
    }, schema)
    .transform((value) => {
      if (value === undefined) {
        return value
      }
      return format(value, constants.SERVER_DATE_FORMAT)
    })

export const createPreprocessedEnumBooleanSchema = (
  // ZodTypeAny because we only care about the input and output
  schema: ZodEffects<ZodTypeAny, boolean, string> | ZodEnum<any>
) =>
  z
    .preprocess((val) => {
      if (typeof val === 'boolean') {
        return val.toString()
      }
      return val
    }, schema)
    .transform((value) => {
      if (value === 'true') {
        return true
      }
      if (value === 'false') {
        return false
      }
      return undefined
    })

export const createPreprocessedNumberSchema = (
  // ZodTypeAny because we only care about the input and output
  schema: ZodOptional<ZodNumber> | ZodNumber | ZodEffects<ZodTypeAny, number, number> | ZodEnum<any>
) =>
  z.preprocess((val) => {
    if (val === '' || val === undefined) {
      return undefined
    }
    return Number(val)
  }, schema)

export const createPreprocessedEnumNumberSchema = (
  // ZodTypeAny because we only care about the input and output
  schema: ZodEffects<ZodTypeAny, number, string> | ZodEnum<any>
) =>
  z
    .preprocess((val) => {
      if (typeof val === 'number') {
        return val.toString()
      }
      return val
    }, schema)
    .transform(Number)

export const createRefineDateSchema = (
  params: Record<
    'minAge' | 'maxAge',
    {
      errorMessage: string
      year: number
    }
  >
) => {
  const { minAge, maxAge } = params
  return z
    .date()
    .refine(
      (date) => !isAfter(date, sub(new Date(), { years: params.minAge.year })),
      minAge.errorMessage
    )
    .refine(
      (date) => !isBefore(date, sub(new Date(), { years: params.maxAge.year })),
      maxAge.errorMessage
    )
}

export const createPreprocessedCancellationDateSchema = <Params extends StepConfigParams>(
  // ZodTypeAny because we only care about the input and output
  schema: ZodObject<ZodRawShape>,
  params: Params,
  stepNamePath: string
) => {
  return z.preprocess((values: any) => {
    const previousContractCancellationDatePath = params.helperFunctions.getParamPath(stepNamePath)

    const previousContractCancellationDateValues = _get(
      values,
      previousContractCancellationDatePath
    )

    if (previousContractCancellationDateValues === undefined) {
      return _set(values, params.helperFunctions.getParamPath(stepNamePath), undefined)
    }

    // In case if we already submit step and still have Invalid Date
    if (
      previousContractCancellationDateValues instanceof Date &&
      !Number.isNaN(previousContractCancellationDateValues)
    ) {
      return _set(
        values,
        params.helperFunctions.getParamPath(stepNamePath),
        previousContractCancellationDateValues
      )
    }

    const [day, month, year] = previousContractCancellationDateValues.split('-')

    if (!day || !month || !year) {
      return _set(values, params.helperFunctions.getParamPath(stepNamePath), undefined)
    }

    const formattedDate = new Date(previousContractCancellationDateValues)

    // In case if year, month, day exist but date is invalid we return Invalid Date object to notify zod
    // in order to use the corresponding status code
    if (!isValid(formattedDate)) {
      return _set(values, params.helperFunctions.getParamPath(stepNamePath), formattedDate)
    }

    return _set(values, previousContractCancellationDatePath, dateUtils.dateToString(formattedDate))
  }, schema)
}

export const getMinAgeValidation = (
  max: number,
  errorMessage?: string | { message?: string; path?: string[] }
) => {
  return createPreprocessedDateSchema(
    z
      .date()
      .max(
        subYears(Date.now(), max),
        errorMessage ?? t('sharedFlows_common:generic.errors.tooOld', { age: max.toString() })
      )
  )
}

export const getMaxAgeValidation = (
  min: number,
  errorMessage?: string | { message?: string; path?: string[] }
) => {
  return createPreprocessedDateSchema(
    z
      .date()
      .min(
        subYears(Date.now(), min),
        errorMessage ?? t('sharedFlows_common:generic.errors.tooYoung', { age: min.toString() })
      )
  )
}

export const getAlphaNumeriCharactersValidation = () =>
  z
    .string()
    .min(1)
    .regex(
      /^(?!\s)(?![\S\s]*\s$)[\d\s()A-Za-z-]+$/,
      t('sharedFlows_common:generic.errors.mustMatchNameFormat')
    )
